import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Card } from "@/components/ui/card";
import { AlertTriangle, GitMerge, Loader2, Users } from "lucide-react";
import { toast } from "sonner";

/**
 * Iter 31c — Dialog admin per trovare e unire dipendenti duplicati.
 * Apre fetch GET /admin/employees/duplicati-candidati e per ogni gruppo
 * permette di scegliere il "nome giusto" (winner) e premere "Unisci".
 */
export default function DuplicatiDipendentiDialog({ open, onOpenChange, onMerged }) {
  const [loading, setLoading] = useState(false);
  const [merging, setMerging] = useState(null); // groupIndex in merge
  const [gruppi, setGruppi] = useState([]);
  const [winnerById, setWinnerById] = useState({}); // {groupIdx: winner_id}

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/employees/duplicati-candidati");
      const gg = data?.gruppi || [];
      setGruppi(gg);
      const initial = {};
      gg.forEach((g, idx) => {
        initial[idx] = g.suggested_winner_id;
      });
      setWinnerById(initial);
    } catch (e) {
      toast.error(
        e?.response?.data?.detail || "Impossibile caricare i duplicati"
      );
      setGruppi([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open) load();
  }, [open]);

  const handleMerge = async (groupIdx) => {
    const group = gruppi[groupIdx];
    const winnerId = winnerById[groupIdx];
    if (!winnerId) {
      toast.error("Scegli il nome giusto prima di unire");
      return;
    }
    const loserIds = group.members
      .map((m) => m.id)
      .filter((id) => id !== winnerId);
    const winnerName = group.members.find((m) => m.id === winnerId)?.full_name;
    if (
      !window.confirm(
        `Confermi l'unione su "${winnerName}" di ${loserIds.length} alt${loserIds.length === 1 ? "ro" : "ri"} dipendente${loserIds.length === 1 ? "" : "i"}?\n\nTutti i rapportini e le ore degli altri verranno riassegnati al nome giusto. I doppi finiscono nel cestino dipendenti.`
      )
    )
      return;
    setMerging(groupIdx);
    try {
      const { data } = await api.post("/admin/employees/merge", {
        winner_id: winnerId,
        loser_ids: loserIds,
      });
      toast.success(
        `Uniti ${data.losers_count} doppi su "${data.winner_full}" — ${data.righe_rapportini_aggiornate} righe rapportini aggiornate.`
      );
      onMerged?.();
      // Ricarica i gruppi
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Unione non riuscita");
    } finally {
      setMerging(null);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <GitMerge className="h-5 w-5 text-amber-600" />
            Trova e unisci dipendenti duplicati
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-3">
          <p className="text-sm text-slate-600">
            Sono mostrati i dipendenti che probabilmente sono la stessa persona
            scritta in più modi (es. <em>BENEDETTI FLAVIO</em> e{" "}
            <em>Benedetti Vallenari Flavio</em>). Per ogni gruppo, seleziona il
            <strong> nome giusto</strong> e premi <strong>Unisci</strong>: tutti
            i rapportini degli altri verranno riassegnati al winner.
          </p>

          {loading && (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-6 w-6 animate-spin text-slate-400" />
            </div>
          )}

          {!loading && gruppi.length === 0 && (
            <Card className="p-6 text-center text-slate-500" data-testid="duplicati-empty">
              <Users className="h-10 w-10 mx-auto mb-2 opacity-40" />
              Nessun duplicato trovato. Anagrafica pulita ✨
            </Card>
          )}

          {!loading &&
            gruppi.map((g, idx) => (
              <Card
                key={g.members.map((m) => m.id).join("|") || idx}
                className="p-4 border-amber-200 bg-amber-50/40"
                data-testid={`duplicati-gruppo-${idx}`}
              >
                <div className="flex items-start gap-2 mb-3">
                  <AlertTriangle className="h-4 w-4 text-amber-600 mt-0.5 shrink-0" />
                  <div className="text-xs text-amber-900">
                    <strong>Motivo:</strong> {g.ragioni.join(" · ")}
                  </div>
                </div>
                <div className="space-y-2">
                  {g.members.map((m) => (
                    <label
                      key={m.id}
                      className={`flex items-center gap-3 rounded-md border px-3 py-2 cursor-pointer transition ${
                        winnerById[idx] === m.id
                          ? "border-blue-500 bg-blue-50 ring-2 ring-blue-200"
                          : "border-slate-200 bg-white hover:border-slate-400"
                      }`}
                    >
                      <input
                        type="radio"
                        name={`winner-${idx}`}
                        checked={winnerById[idx] === m.id}
                        onChange={() =>
                          setWinnerById((w) => ({ ...w, [idx]: m.id }))
                        }
                        data-testid={`duplicati-${idx}-winner-${m.id}`}
                      />
                      <div className="flex-1 min-w-0">
                        <div className="font-semibold truncate">
                          {m.full_name}
                          {winnerById[idx] === m.id && (
                            <span className="ml-2 text-[10px] font-bold uppercase tracking-wider rounded bg-blue-600 text-white px-1.5 py-0.5">
                              nome giusto
                            </span>
                          )}
                        </div>
                        <div className="text-xs text-slate-500">
                          {m.codice && <span>codice {m.codice} · </span>}
                          {m.rapportini_count} rapportin
                          {m.rapportini_count === 1 ? "o" : "i"}
                          {!m.mostra_nei_rapportini && (
                            <span className="ml-1 text-slate-400">
                              · nascosto
                            </span>
                          )}
                        </div>
                      </div>
                    </label>
                  ))}
                </div>
                <DialogFooter className="mt-3 sm:justify-end">
                  <Button
                    onClick={() => handleMerge(idx)}
                    disabled={merging === idx}
                    className="bg-amber-600 hover:bg-amber-700"
                    data-testid={`duplicati-${idx}-merge-button`}
                  >
                    {merging === idx ? (
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    ) : (
                      <GitMerge className="h-4 w-4 mr-2" />
                    )}
                    Unisci {g.members.length - 1} su 1
                  </Button>
                </DialogFooter>
              </Card>
            ))}
        </div>

        <DialogFooter className="sm:justify-between mt-3">
          <Button variant="outline" onClick={load} disabled={loading}>
            Ricarica
          </Button>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Chiudi
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
